import { capitalize, cloneDeep, groupBy, isEmpty, isNumber, orderBy, toNumber } from "lodash";
import { TcomputedKeyParam } from "../types/computedKeyParam.type";
import { TKeyParamLayout } from "../types/exam.type";
import { TtrainingKeyParameter } from "../types/trainingKeyParameter.type";
import { Ttraining } from "../types/training.type";
import { TtrainingKeyParameterItem } from "../types/TrainingKeyParameterItem.type";
import { Chukkers } from "../types/chukkers.type";
import { Scale } from "../../pages/trainings/ExamView";
import { convertSpeedToNb, convertStrideLength, getSpeedForChart } from "./conversion";
import { TUser } from "../types/user.type";
import { XAxisPlotBandsEventsOptions } from "highcharts";
import dayjs from "dayjs";
import { t } from "i18next";

export type TsavePreselects = { "trainer"?: string, "insight_parameter": string, "order_number": number }


export type TParamsChartItem = {
    param: TcomputedKeyParam,
    trainings: (Ttraining & { raw_numeric_value: number, formatted_value?: string })[]
}
export type TParamsChartData = TParamsChartItem[]

export function mapTypeToTrainingKeys(alltrainingKeys: TtrainingKeyParameter, preselects: TKeyParamLayout[]) {

    let data: TcomputedKeyParam[] = []
    if (!isEmpty(alltrainingKeys?.items) && !isEmpty(preselects)) {

        let computedKeyParams = alltrainingKeys.items?.[0]?.computed_key_parameters ?? []

        preselects.forEach(preselect => {
            let id = preselect.insight_parameter?.["@id"]

            let findTarget = computedKeyParams.find(key => key.key_parameter_layout?.insight_parameter?.["@id"] === id)
            if (findTarget) {
                data.push(findTarget)
            } else {
                console.log({ preselect });

            }
        })
    }
    return data
}

export const groupByCategory = (alltrainingKeys: TtrainingKeyParameter) => {
    let computedKeyParams = alltrainingKeys.items?.[0]?.computed_key_parameters ?? []
    let data: Record<string, TcomputedKeyParam[]> = {}
    if (computedKeyParams.length > 0) {
        data = groupBy(computedKeyParams, function (key) {
            return key.key_parameter_layout.insight_parameter.category
        })
    }
    return data
}

export const replaceWithNewKey = (alltrainingKeys: TtrainingKeyParameter, newId: string, oldKey: TcomputedKeyParam, currentKeys: TcomputedKeyParam[]) => {
    let computedKeyParams = alltrainingKeys.items?.[0]?.computed_key_parameters ?? []
    let data: TcomputedKeyParam[] = []
    if (isEmpty(computedKeyParams)) return data

    data = cloneDeep(currentKeys)

    let newKey = computedKeyParams.find(e => e.key_parameter_layout?.insight_parameter?.["@id"] === newId)

    let oldIndex = currentKeys.findIndex(e => e.key_parameter_layout?.insight_parameter?.["@id"] === oldKey.key_parameter_layout?.insight_parameter?.["@id"])

    if (oldIndex !== -1) {

        if (newKey) {
            newKey.key_parameter_layout.order_number = data[oldIndex].key_parameter_layout.order_number

            data[oldIndex] = newKey
        } else {
            data.splice(oldIndex, 1)
        }

    }


    return data
}


export const formatComputedKeyToSave = (computedKeys: TcomputedKeyParam[], trainer?: string): TsavePreselects[] => {

    return computedKeys.map((key, i) => ({
        trainer,
        insight_parameter: key.key_parameter_layout.insight_parameter["@id"],
        order_number: isNumber(key.key_parameter_layout?.order_number) ? key.key_parameter_layout?.order_number : i
    }))
}

export function getRandomElements(array: TcomputedKeyParam[], numElements: number) {
    const randomElements = [];

    // Generate random indexes and push corresponding elements into randomElements array
    for (let i = 0; i < numElements; i++) {
        const randomIndex = Math.floor(Math.random() * array.length);
        randomElements.push(array[randomIndex]);
    }


    return randomElements;
}

export const reoderElements = (elements: TcomputedKeyParam[], lastIndex: number) => {
    return elements.map((element, index) => ({
        ...element,
        key_parameter_layout: {
            ...element.key_parameter_layout,
            order_number: lastIndex + (index + 1)
        }
    }))
}


export const prepareTrainingParamForCharts = (items: TtrainingKeyParameterItem[]) => {
    if (isEmpty(items)) return []

    //Get available Params
    let random_item = items.at(0)

    let computedParams = random_item?.computed_key_parameters ?? []

    let data: TParamsChartData = []

    if (isEmpty(computedParams)) {
        console.warn('No computed param found');
        return []
    }



    computedParams.forEach(param => {
        let payload: TParamsChartItem = {
            param,
            trainings: []
        }

        items.forEach(i => {

            let foundKey = i.computed_key_parameters.find(e => e.key_parameter_layout["@id"] === param.key_parameter_layout["@id"])

            //We ignore null values or 0
            if (foundKey?.raw_numeric_value && foundKey.raw_numeric_value !== 0) {

                payload.trainings.push({
                    ...i.training,
                    raw_numeric_value: foundKey?.raw_numeric_value ?? 0,
                    formatted_value: foundKey?.formatted_value ?? ''
                })

            }


        })

        data.push(payload)
    })

    return data


}

export const renderUnit = (unit?: string) => {
    if (!unit) return ''

    if (unit === 'mm:ss.ms') {
        return 's'
    }
    return unit
}

export function getUnitForChukkerKey(key: string, metric: TUser['unit_system']): string {
    if (key.includes("number")) return ""; // no unit
    if (key.includes("time") || key.includes("duration")) return capitalize(t("units:minutes"));
    if (key.includes("hr") || key.includes("bpm")) return t("units:bpm");
    if (key.includes("distance")) return capitalize(t(metric === 'metric' ? 'trainingSection:km' : "navbar:units:mi"));
    if (key.includes("speed") || key.includes('v200')) return capitalize(t(metric === 'metric' ? "units:km\/h" : "units:mph"));
    if (key.includes("stride_frequency")) return capitalize(t("units:stridesPerSec"));
    if (key.includes("stride_length")) return capitalize(t(metric === 'metric' ? 'units:metersPerStr' : 'ft\/str'));
    if (key.includes('acceleration') || key.includes('braking') || key.includes('best_sprint')) return capitalize(t("unit:ms2"));


    console.warn(`❓ Unknown unit for key: "${key}"`);
    return "";
}


export function formatValueBasedOnChukkerKeyUnit(value: any, key: string, metric: TUser['unit_system'], userSpeedUnit: string) {
    if (key.includes('number')) return value
    if (key.includes("time") || key.includes("duration")) return value ? dayjs.duration(value, 'seconds').format('mm:ss') : "";
    if (key.includes("speed") || key.includes('v200')) return getSpeedForChart(toNumber(value), userSpeedUnit);
    if (key.includes("stride_frequency")) return value ? (toNumber(value) / 50).toFixed(2) : "";
    if (key.includes("distance")) return convertSpeedToNb(metric, value).toFixed(2)
    if (key.includes("stride_length")) return value ? (toNumber(convertStrideLength(metric, toNumber(value)))).toFixed(2) : "";
    if (key.includes('acceleration') || key.includes('braking') || key.includes('best_sprint')) return (toNumber(value) / 600).toFixed(2)
    return value
}


export function createPlotBandsFromChukkers(chukkers: Chukkers, time: number[], distance: number[], scale: Scale, events: XAxisPlotBandsEventsOptions, selectedPlotBand: number | null, plotIndex?: string | null): Highcharts.XAxisPlotBandsOptions[] {

    const PLOT_COLOR = "rgba(229, 228, 226,.3)"

    if (isEmpty(chukkers)) return []


    let values = scale === 'DISTANCE' ? distance : time

    let orderedChukkers = orderBy(chukkers, chukker => chukker.manual_index_start || chukker.index_start)


    return orderedChukkers.map((chukker, index) => ({
        color: selectedPlotBand === chukker.id ? 'rgba(155, 20, 26, .1)' : PLOT_COLOR, // Color value
        from: values[(chukker.manual_index_start || chukker.index_start)], // Start of the plot band
        to: values[(chukker.manual_index_end || chukker.index_end)],// End of the plot band
        id: chukker.id.toString(),
        // label: ,
        borderColor: selectedPlotBand === chukker.id ? 'rgba(155, 20, 26, .4)' : 'none',
        borderWidth: selectedPlotBand === chukker.id ? 2 : 0,
        label: {
            text: plotIndex ? plotIndex : `${index + 1}`
        },
        zIndex: 999,
        className: 'dotted-band',
        events
    }))

}
